import React from "react";
import { Settings as SettingsIcon, Ruler, LogOut } from "lucide-react";
import { useUnits } from "@/lib/useUnits";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SnapshotTimeline from "@/components/SnapshotTimeline";
import CheckinQuestionsBuilder from "@/components/CheckinQuestionsBuilder";
import SubscriptionCard from "@/components/SubscriptionCard";
import MyCoachCard from "@/components/MyCoachCard";

export default function Settings() {
  const { units, setUnits } = useUnits();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const change = async (next) => {
    if (next === units) return;
    await setUnits(next);
    toast.success(`Units set to ${next === "imperial" ? "imperial (lb / in / ft)" : "metric (kg / cm)"}`);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <div className="overline text-blaze">/ Settings</div>
        <h1 className="font-heading text-5xl uppercase mt-1">Preferences</h1>
        <p className="text-zinc-400 mt-2">Signed in as <span className="text-zinc-200">{user?.email}</span></p>
      </div>

      <div className="obs-card" data-testid="settings-units">
        <div className="flex items-center gap-3 mb-4">
          <Ruler className="h-5 w-5 text-blaze" />
          <div>
            <div className="overline">Display units</div>
            <h3 className="font-heading text-2xl uppercase">Weight · height</h3>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Data is always stored in metric internally — this only controls what you see in the app.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="settings-units-metric"
            onClick={() => change("metric")}
            className={`text-left p-4 rounded-sm border transition-colors ${
              units === "metric"
                ? "border-blaze bg-blaze/10"
                : "border-obsidian-600 hover:border-zinc-500"
            }`}
          >
            <div className="overline mb-1">Metric</div>
            <div className="font-heading text-xl uppercase">kg · cm</div>
            <div className="text-[11px] text-zinc-400 mt-1 uppercase tracking-widest">
              Kilograms · centimeters
            </div>
          </button>
          <button
            data-testid="settings-units-imperial"
            onClick={() => change("imperial")}
            className={`text-left p-4 rounded-sm border transition-colors ${
              units === "imperial"
                ? "border-blaze bg-blaze/10"
                : "border-obsidian-600 hover:border-zinc-500"
            }`}
          >
            <div className="overline mb-1">Imperial</div>
            <div className="font-heading text-xl uppercase">lb · ft · in</div>
            <div className="text-[11px] text-zinc-400 mt-1 uppercase tracking-widest">
              Pounds · feet · inches
            </div>
          </button>
        </div>
      </div>

      <div className="obs-card">
        <div className="flex items-center gap-3 mb-2">
          <SettingsIcon className="h-5 w-5 text-blaze" />
          <h3 className="font-heading text-2xl uppercase">Account</h3>
        </div>
        <ul className="text-sm text-zinc-300 space-y-1">
          <li>Email: <span className="text-zinc-100">{user?.email}</span></li>
          <li>Role: <span className="text-zinc-100 capitalize">{user?.role || (user?.is_coach ? "coach" : "client")}</span></li>
          <li>Plan: <span className="text-zinc-100">{user?.subscription?.status || "—"}</span></li>
        </ul>
      </div>

      <SubscriptionCard />

      {!user?.is_coach && <MyCoachCard />}

      {user?.is_coach && <CheckinQuestionsBuilder />}

      <SnapshotTimeline />

      <div className="obs-card border-red-500/30" data-testid="settings-logout-card">
        <div className="flex items-center gap-3 mb-2">
          <LogOut className="h-5 w-5 text-red-400" />
          <div>
            <div className="overline">Session</div>
            <h3 className="font-heading text-2xl uppercase">Sign out</h3>
          </div>
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Log out of Ripple Effect on this device. Your data stays safe — sign back in any time.
        </p>
        <button
          type="button"
          onClick={() => {
            if (window.confirm("Sign out of Ripple Effect on this device?")) {
              logout();
              // Force landing so protected routes don't briefly render stale
              // shell before the auth guard kicks in.
              navigate("/", { replace: true });
            }
          }}
          data-testid="settings-logout-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-red-500/40 text-red-400 hover:bg-red-500/10 uppercase tracking-widest text-xs rounded-sm transition-colors"
        >
          <LogOut className="h-4 w-4" /> Log out now
        </button>
      </div>
    </div>
  );
}
